import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  openNav: {
    openSelectors: [
      '[data-tutorial-pres="open-nav"]',
      '[aria-label="메인 프레젠테이션 새 탭으로 열기 (Shift + O)"]',
      '.cp-topnav-clock-btn',
    ],
    openLabel: '메인 프레젠테이션',
    accessLabel: '상단 네비바',
  },
  page: {
    pageLabel: '메인 프레젠테이션',
    accessLabel: '상단 네비바 메인 프레젠테이션 버튼(새 탭)',
    openLabel: '메인 프레젠테이션',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'open-main-presentation': R.openNav,
  'guide-overview': R.page,
  'guide-clock': R.page,
  'guide-datenav': R.page,
  'open-assignment': R.page,
  'assignment-panel': R.page,
  'open-point': R.page,
  'point-panel': R.page,
  'open-board': R.page,
  'board-modes': R.page,
  'close-board': R.page,
  'open-webcam': R.page,
  'webcam-pause': R.page,
  'timer': R.page,
  'timer-modal': R.page,
  'bgm': R.page,
  'bgm-stop': R.page,
  'dice': R.page,
  'dice-modal': R.page,
  'caption': R.page,
  'caption-stop': R.page,
  'fullscreen': R.page,
};

const mainPresentationRaw: TutorialDefinition = {
  id: 'main-presentation',
  title: '메인 프레젠테이션',
  summary:
    '네비바에서 메인 프레젠테이션을 연 뒤, 시간안내·과제·포인트·칠판·웹캠·타이머·BGM·뽑기·자막·전체화면 흐름을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '메인 프레젠테이션 튜토리얼을 시작해요! 먼저 네비바 버튼으로 수업용 새 탭을 연 뒤, 화면 전환과 칠판·웹캠·타이머를 순서대로 써 볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '메인 프레젠테이션을 시작해요!',
      placement: 'center',
    },
    {
      id: 'open-main-presentation',
      mascot:
        '상단 네비바의 메인 프레젠테이션 버튼을 누르거나, 단축키 Shift+O로 수업용 새 탭을 열어 주세요. 새 탭에서 이어서 안내해요.',
      targetSelector:
        '[data-tutorial-pres="open-nav"], .cp-topnav-clock-btn, [aria-label="메인 프레젠테이션 새 탭으로 열기 (Shift + O)"]',
      placement: 'bottom',
      section: '[프레젠] 시작',
      sectionMascot: '네비바에서 메인 프레젠테이션을 여는 구간이에요.',
    },
    {
      id: 'guide-overview',
      mascot:
        '기본은 "시간안내" 화면이에요. 위쪽 교시 블럭과 가운데 시각으로 현재 수업을 확인해요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-dash-presentation-guide, [data-tutorial-pres="guide"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[프레젠] 시간안내',
      sectionMascot: '기본 수업·시간안내 화면을 알아보는 구간이에요.',
    },
    {
      id: 'guide-clock',
      mascot:
        '가운데 큰 시각과 교시 안내가 실시간으로 바뀌어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-dash-presentation-clock-area, [data-tutorial-pres="clock"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'guide-datenav',
      mascot:
        '아래 날짜 이동으로 다른 날 시간표를 볼 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-dash-presentation-datenav, [data-tutorial-pres="datenav"]',
      manualAdvance: true,
    },
    {
      id: 'open-assignment',
      mascot: '"과제 카드" 탭을 눌러 과제관리 화면으로 바꿔 주세요.',
      targetSelector: '[data-tutorial-pres="tab-assignment"]',
      advanceWhenSelectorExists: '.cp-dash-presentation-page--assignment-card',
      /* 프리 이용권: 탭이 잠겨 있으면 유료 안내 후 "다음"으로 포인트 단계로 건너뜀 */
      conditionalOverride: {
        whenSelectorExists: '[data-tutorial-pres="tab-assignment"].is-pass-locked',
        override: {
          mascot:
            '과제 카드는 유료 이용권 사용자 전용 기능이에요. 이용권을 등록하면 수업 중 과제 카드를 바로 띄울 수 있어요. "다음"을 눌러 주세요.',
          advanceWhenSelectorExists: undefined,
          manualAdvance: true,
          blockTargetClick: true,
          manualAdvanceSkipToStepId: 'open-point',
        },
      },
      section: '[프레젠] 과제 · 포인트',
      sectionMascot: '수업 중 과제·포인트를 바로 쓰는 구간이에요.',
    },
    {
      id: 'assignment-panel',
      mascot:
        '여기서 과제 카드를 확인하고 완료 처리할 수 있어요. 전자칠판 화면에 띄워두고 멤버들이 각자 자신의 과제를 체크할 때 사용하면 유용해요.',
      targetSelector:
        '.cp-dash-presentation-assignment-card-host, [data-tutorial-pres="assignment-host"]',
      placement: 'bottom',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'open-point',
      mascot: '"포인트" 탭을 눌러 포인트·코인·아이템 화면으로 바꿔 주세요.',
      targetSelector: '[data-tutorial-pres="tab-point"]',
      advanceWhenSelectorExists: '.cp-dash-presentation-page--point-item',
      conditionalOverride: {
        whenSelectorExists: '[data-tutorial-pres="tab-point"].is-pass-locked',
        override: {
          mascot:
            '포인트는 유료 이용권 사용자 전용 기능이에요. 이용권을 등록하면 수업 중 포인트·코인·아이템을 바로 지급할 수 있어요. "다음"을 눌러 주세요.',
          advanceWhenSelectorExists: undefined,
          manualAdvance: true,
          blockTargetClick: true,
          manualAdvanceSkipToStepId: 'open-board',
        },
      },
    },
    {
      id: 'point-panel',
      mascot:
        '여기서 멤버별 포인트, 코인, 아이템을 지급할 수 있어요. 전자칠판 화면에 띄워두고 수업 중 빠르게 포인트 바로 지급할 수 있어요.',
      targetSelector:
        '.cp-dash-presentation-point-item-host, [data-tutorial-pres="point-host"]',
      placement: 'bottom',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'open-board',
      mascot: '칠판 아이콘을 눌러 임시 칠판(판서) 화면을 열어 주세요.',
      targetSelector:
        '.cp-dash-presentation-view-tab--board-icon, [data-tutorial-pres="tab-board"]',
      advanceWhenSelectorExists: '.cp-dash-presentation-page--board',
      section: '[프레젠] 칠판 · 웹캠',
      sectionMascot: '판서와 실물화상기를 쓰는 구간이에요.',
    },
    {
      id: 'board-modes',
      mascot:
        '"편집"과 "보기"로 칠판 모드를 바꿀 수 있어요. 슬라이드에서 칠판 기능도 써 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-dash-presentation-board-seg, [data-tutorial-pres="board-modes"], .cp-dash-presentation-board-host, [data-tutorial-pres="board-slide"], .cp-dash-presentation-board-draw-overlay, [data-tutorial-pres="board-draw"]',
      placement: 'below-nav',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      manualAdvanceIfExists: '.cp-dash-presentation-board-seg',
    },
    {
      id: 'close-board',
      mascot: '칠판 아이콘(끄기)을 눌러 칠판을 닫아 주세요.',
      targetSelector:
        '[aria-label="칠판 모드 끄기"], [data-tutorial-pres="board-close"]',
      advanceWhenSelectorGone: '.cp-dash-presentation-page--board',
    },
    {
      id: 'open-webcam',
      mascot:
        '웹캠(실물화상기) 버튼을 눌러 미리보기를 켜 보세요. 권한 허용이 필요할 수 있어요.',
      targetSelector: '[data-tutorial-pres="webcam"]',
      advanceWhenSelectorExists:
        '.cp-dash-presentation-webcam-overlay, [data-tutorial-pres="webcam-overlay"], .cp-program-slide-webcam-nav-block.is-webcam-on',
      manualAdvanceIfExists:
        '.cp-dash-presentation-webcam-overlay, [data-tutorial-pres="webcam-overlay"], .cp-program-slide-webcam-nav-block.is-webcam-on',
      allowManualAdvanceActions: true,
      section: '[프레젠] 도구 · 전체화면',
      sectionMascot: '웹캠·타이머·BGM·뽑기·자막·전체화면을 쓰는 구간이에요.',
    },
    {
      id: 'webcam-pause',
      mascot:
        '화면을 클릭하거나 상단의 일시정지 버튼을 누르면 화면이 멈춰요. 정지한 뒤 판서로 표시해 볼 수 있어요. "다음"을 누르면 웹캠이 닫힌 뒤 이어가요.',
      targetSelector:
        '[data-tutorial-pres="webcam-pause"], [aria-label="미리보기 일시정지"], [aria-label="미리보기 재생"], [data-tutorial-pres="webcam-screen"], .cp-dash-presentation-webcam-crop, .cp-dash-presentation-webcam-overlay, [data-tutorial-pres="webcam-overlay"], .cp-program-slide-webcam-draw-toolbar, .cp-program-slide-webcam-draw-toolbar-host',
      placement: 'below-nav',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-pres="webcam-stop"], [aria-label="미리보기 정지"]',
      targetMissingHint:
        '웹캠 미리보기가 켜져 있어야 해요. 앞에서 웹캠 버튼으로 켠 뒤 다시 진행해 주세요.',
    },
    {
      id: 'timer',
      mascot: '타이머 버튼을 눌러 수업용 타이머를 열어 주세요.',
      targetSelector: '[data-tutorial-pres="timer"]',
      advanceWhenSelectorExists:
        '[data-tutorial-pres="timer-modal"] .cp-edu-timer-content, .cp-edu-timer-overlay .cp-edu-timer-content',
    },
    {
      id: 'timer-modal',
      mascot:
        '타이머 모달이에요. 시간을 설정하고 실행해 볼 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pres="timer-modal"] .cp-edu-timer-content, .cp-edu-timer-overlay .cp-edu-timer-content',
      placement: 'left',
      advanceWhenSelectorGone:
        '[data-tutorial-pres="timer-modal"] .cp-edu-timer-content, .cp-edu-timer-overlay .cp-edu-timer-content, [data-tutorial-pres="timer-modal"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-pres="timer-close"], [data-tutorial-pres="timer-modal"] [title="닫기"], [data-tutorial-pres="timer-modal"] [aria-label="닫기"]',
    },
    {
      id: 'bgm',
      mascot: 'BGM 버튼을 눌러 배경음 선택 창을 열어 주세요.',
      targetSelector: '[data-tutorial-pres="bgm"]',
      followUp: {
        targetSelector:
          '[data-tutorial-pres="bgm-modal"] .cp-program-slide-bgm-modal, .cp-program-slide-bgm-modal',
        mascot:
          'BGM 모달이에요. 분위기·트랙(또는 랜덤 플레이) 중 하나를 눌러 재생해 주세요. 트랙을 고르면 다음으로 진행돼요.',
        placement: 'left',
        advanceSelector: '[data-tutorial-slide="bgm-pick"]',
      },
    },
    {
      id: 'bgm-stop',
      mascot:
        '재생 중에는 정지 버튼으로 BGM을 끌 수 있어요. 정지 버튼을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pres="bgm-stop"], [data-tutorial-slide="bgm-stop"], [aria-label="BGM 정지"]',
      advanceSelector:
        '[data-tutorial-pres="bgm-stop"], [data-tutorial-slide="bgm-stop"], [aria-label="BGM 정지"]',
      advanceImmediately: true,
      targetMissingHint:
        'BGM이 재생 중이어야 해요. 앞에서 트랙을 고른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'dice',
      mascot: '뽑기(주사위) 버튼을 눌러 뽑기 모달을 열어 주세요.',
      targetSelector: '[data-tutorial-pres="dice"]',
      advanceWhenSelectorExists:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
    },
    {
      id: 'dice-modal',
      mascot:
        '뽑기 모달이에요. 학생·자리·팀을 뽑아 볼 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
      placement: 'left',
      advanceWhenSelectorGone:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-pres="dice-modal"] [aria-label="닫기"], .cp-program-slide-dice-modal [aria-label="닫기"], .cp-slide-dice-modal [aria-label="닫기"]',
    },
    {
      id: 'caption',
      mascot:
        '실시간 자막 버튼을 눌러 자막을 켜 주세요. 브라우저·마이크 권한이 필요할 수 있어요.',
      targetSelector: '[data-tutorial-pres="caption"]:not([disabled])',
      advanceWhenSelectorExists:
        '[data-tutorial-pres="caption-stop"], [aria-label="자막 끄기"]',
      manualAdvanceIfExists: '[data-tutorial-pres="caption-stop"], [aria-label="자막 끄기"]',
      targetMissingHint:
        '이 환경에서는 실시간 자막 버튼을 쓸 수 없을 수 있어요. 튜토리얼 목록에서 이어가거나 화면을 다시 불러와 주세요.',
    },
    {
      id: 'caption-stop',
      mascot:
        '자막을 끌 때는 정지 버튼을 눌러 주세요. 정지 버튼을 누르거나 "다음"을 누르면 자막이 꺼진 뒤 이어가요.',
      targetSelector: '[data-tutorial-pres="caption-stop"], [aria-label="자막 끄기"]',
      advanceSelector: '[data-tutorial-pres="caption-stop"], [aria-label="자막 끄기"]',
      advanceImmediately: true,
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-pres="caption-stop"], [aria-label="자막 끄기"]',
      targetMissingHint:
        '자막이 켜져 있어야 해요. 앞에서 자막을 켠 뒤 다시 진행하거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'fullscreen',
      mascot:
        '수업 중에는 전체화면이 편해요. 전체화면 버튼을 눌러 보거나, 이미 익숙하면 "다음"으로 넘어가도 돼요.',
      targetSelector: '[data-tutorial-pres="fullscreen"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'outro',
      mascot:
        '메인 프레젠테이션 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const mainPresentation: TutorialDefinition = {
  ...mainPresentationRaw,
  steps: mainPresentationRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(
        recover,
        step.targetMissingHint ??
          '메인 프레젠테이션 새 탭에서 진행해 주세요. 튜토리얼 목록에서 다시 시작하면 새 탭이 열려요.'
      ),
    };
  }),
};

export default mainPresentation;
